import * as Sentry from '@sentry/browser';
import type { ErrorEvent, Scope } from '@sentry/browser';
import { sanitizeSentryEvent } from './sanitize-sentry-event';

type BrowserObservabilityInitConfig = {
  dsn: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  serviceTag: string;
};

export function initWebObservability(config: Omit<BrowserObservabilityInitConfig, 'serviceTag'>) {
  initBrowserObservability({
    ...config,
    serviceTag: 'trailup-web',
  });
}

export function initAdminObservability(config: Omit<BrowserObservabilityInitConfig, 'serviceTag'>) {
  initBrowserObservability({
    ...config,
    serviceTag: 'trailup-admin',
  });
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  Sentry.withScope((scope: Scope) => {
    if (context) {
      scope.setContext('client', context);
    }

    Sentry.captureException(error);
  });
}

function initBrowserObservability(config: BrowserObservabilityInitConfig): void {
  if (!config.dsn.trim()) {
    throw new Error('Sentry is enabled but sentryDsn is empty');
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? 'development',
    release: config.release ?? 'local',
    tracesSampleRate: normalizeSampleRate(config.tracesSampleRate, 0.1),
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event: ErrorEvent) {
      return sanitizeSentryEvent(event) as ErrorEvent;
    },
  });

  Sentry.setTag('service', config.serviceTag);
}

function normalizeSampleRate(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }

  if (value < 0 || value > 1) {
    return fallback;
  }

  return value;
}
